import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '../../../Contexts/AuthContext';
import { accountService } from '../../../Api/Services/accountService';
import { addressService } from '../../../Api/Services/addressService';
import { Customer } from '../../../Models/Customer';
import { Address } from '../../../Models/Address';
import { EditUserDataType } from '../../../Enums/EditUserDataType';
import { clearUserSession } from "../../../Utils/AuthUtils";
import { FaFile, FaPlus, FaUser } from 'react-icons/fa';
import { toast } from "react-toastify";
import SmartNavbar from '../../../Components/SmartNavbar/SmartNavbar';
import IndividualPersonForm from '../../../Components/IndividualPersonForm/IndividualPersonForm';
import LegalEntityForm from '../../../Components/LegalEntityForm/LegalEntityForm';
import AddressItem from '../../../Components/AddressItem/AddressItem';
import AddressForm from '../../../Components/AddressForm/AddressForm';
import Footer from '../../../Components/Footer/Footer';
import EditUserDataFlow from '../../../Components/EditUserDataFlow/EditUserDataFlow';
import DeleteAccountModal from '../../../Components/DeleteAccountModal/DeleteAccountModal';
import Button from "../../../Components/Button/Button";
import PageTitle from "../../../Components/PageTitle/PageTitle";

type UserDataLocationState = {
  openAddressForm?: boolean;
  returnTo?: string;
};

const UserDataPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGoogleUser } = useAuth();
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showAddressForm, setShowAddressForm] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedAddress, setEditedAddress] = useState<Address | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAddressesLoading, setIsAddressesLoading] = useState<boolean>(false);
  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(null);

  const [method, setMethod] = useState<EditUserDataType>(EditUserDataType.Email);
  const [isEditUserDataFlowOpen, setIsEditUserDataFlowOpen] = useState(false);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    loadCustomer();
  }, []);

  useEffect(() => {
    const state = location.state as UserDataLocationState | null;
    if (state?.openAddressForm) {
      setShowAddressForm(true);
    }
    if (state?.returnTo) {
      setReturnTo(state.returnTo);
    }
  }, [location.state]);

  const loadCustomer = async () => {
    setIsLoading(true);
    const user = await accountService.get();
    setEmail(user.email);
    setPhoneNumber(user.phoneNumber);
    setCustomer(user.customer);
    setAddresses(user.customer?.addresses ?? []);
    setIsLoading(false);
  };

  const loadAddresses = async (customerId: string) => {
    setIsAddressesLoading(true);
    try {
      const data = await addressService.getByCustomer({ customerId });
      setAddresses(data);
    } catch {
      toast.error("Erro ao carregar endereços.");
    } finally {
      setIsAddressesLoading(false);
    }
  };

  const handleEditUserData = (method: EditUserDataType) => {
    setIsEditUserDataFlowOpen(true);
    setMethod(method);
  };

  const handleSaveForm = () => {
    loadCustomer();
  };

  const handleCloseAddressForm = () => {
    setShowAddressForm(false);
    setIsEditing(false);
    setEditedAddress(null);
    setReturnTo(null);
  };

  const handleSaveAddressForm = (createdAddressId?: string | null) => {
    if (returnTo && createdAddressId) {
      navigate(returnTo, {
        state: { newAddressId: createdAddressId, returnStep: 1 }
      });
      return;
    }

    handleCloseAddressForm();
    if (customer?.id) {
      loadAddresses(customer.id);
    }
  };

  const handleEditAddress = (address: Address) => {
    setIsEditing(true);
    setEditedAddress(address);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = async (address: Address) => {
    if (deletingAddressId) {
      return;
    }

    setDeletingAddressId(address.id);
    try {
      const deleted = await addressService.delete(address.id);
      if (deleted) {
        toast.success("Endereço excluído com sucesso.");
        if (customer?.id) {
          await loadAddresses(customer.id);
        }
      } else {
        toast.error("Não foi possível excluir o endereço.");
      }
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message ?? "Não foi possível excluir o endereço.");
    } finally {
      setDeletingAddressId(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) {
      return;
    }

    setIsDeletingAccount(true);
    try {
      const result = await accountService.deleteAccount();
      if (result) {
        toast.success("Conta excluída com sucesso.");
        clearUserSession();
        navigate("/");
      } else {
        toast.error("Falha ao excluir a conta, tente novamente mais tarde.");
      }
    } catch {
      toast.error("Erro ao excluir a conta.");
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div className="bg-gray-100 page-gutter py-4 sm:py-14">
      <div className="my-6 sm:my-0 sm:mb-8">
        <SkeletonBlock height={32} width={180} />
      </div>
      <div className="w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="w-full bg-white rounded-md p-4 space-y-4">
          <div className="flex items-center gap-2">
            <SkeletonBlock height={20} width={140} />
          </div>
          <div className="flex gap-3">
            <SkeletonBlock height={44} />
            <SkeletonBlock height={44} />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonBlock key={index} height={18} width={`${70 + index * 5}%`} />
            ))}
          </div>
        </div>

        <div className="w-full bg-white rounded-md p-4 space-y-4">
          <div className="flex items-center gap-2">
            <SkeletonBlock height={20} width={140} />
          </div>
          <SkeletonBlock height={32} width={220} />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <SkeletonBlock key={index} height={70} />
            ))}
          </div>
          <SkeletonBlock height={44} width={180} />
        </div>
      </div>
    </div>
  );

  const renderAddressesSkeleton = () => (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <SkeletonBlock key={index} height={70} />
      ))}
    </div>
  );

  return (
    <div>
      <SmartNavbar />
      {isLoading ? (
        renderSkeleton()
      ) : (
        <div className="bg-gray-100 page-gutter py-4 sm:py-14">
          <div className="my-6 sm:my-0 sm:mb-8">
            <PageTitle>{"MEUS DADOS"}</PageTitle>
          </div>
          <div className="w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="w-full bg-white rounded-md p-3">
              <div>
                <div className="font-semibold text-gray-700 p-2 mb-4 flex items-center gap-2">
                  <FaFile className="text-primary-600" />
                  <h4 className="text-md font-bold text-gray-700">{"DADOS BÁSICOS"}</h4>
                </div>

                {!isGoogleUser && (
                  <div className="flex gap-3 mb-4 text-sm">
                    <Button variant="secondary"
                      className="flex-1 py-3"
                      onClick={() => handleEditUserData(EditUserDataType.Email)}
                    >
                      {"ALTERAR E-MAIL"}
                    </Button>
                    <Button variant="secondary"
                      className="flex-1 py-3"
                      onClick={() => handleEditUserData(EditUserDataType.Password)}
                    >
                      {"ALTERAR SENHA"}
                    </Button>
                  </div>
                )}

                {customer?.individualPersonId && customer?.individualPerson ? (
                  <IndividualPersonForm
                    individualPerson={customer.individualPerson}
                    email={email}
                    phoneNumber={phoneNumber}
                    onSave={handleSaveForm}
                  />
                ) : customer?.legalEntityId && customer?.legalEntity ? (
                  <LegalEntityForm
                    legalEntity={customer.legalEntity}
                    email={email}
                    phoneNumber={phoneNumber}
                    onSave={handleSaveForm}
                  />
                ) : null}

                <Button variant="secondary"
                  className="mt-3 w-full py-2"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isDeletingAccount}
                >
                  {"Excluir minha conta"}
                </Button>
              </div>
            </div>

            <div className="w-full bg-white rounded-md p-3">
              <div className="font-semibold text-gray-700 p-2 mb-4 flex items-center gap-2">
                <FaUser className="text-primary-600" />
                <h3 className="text-md font-bold text-gray-700">{"ENDEREÇOS"}</h3>
              </div>

              <button
                className="flex items-center text-primary-600 text-xs font-bold mb-6 gap-1 hover:underline"
                onClick={() => setShowAddressForm(true)}
              >
                <FaPlus />
                {"CADASTRAR NOVO ENDEREÇO"}
              </button>

              {isAddressesLoading ? (
                renderAddressesSkeleton()
              ) : !showAddressForm ? (
                addresses.length > 0 ? (
                  <div
                    className={`pr-1 ${
                      addresses.length > 4 ? "max-h-[670px] overflow-y-auto" : ""
                    }`}
                  >
                    <div className="flex flex-col gap-2">
                      {addresses.map((address, index) => (
                        <AddressItem
                          key={address.id}
                          address={address}
                          index={index}
                          onEdit={handleEditAddress}
                          onDelete={handleDeleteAddress}
                          isDeleting={deletingAddressId === address.id}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">{"Sem endereços cadastrados."}</p>
                )
              ) : (
                <AddressForm
                  isEditing={isEditing}
                  editedAddress={editedAddress}
                  existingAddresses={addresses}
                  onClose={handleCloseAddressForm}
                  onSave={handleSaveAddressForm}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <Footer />

      <EditUserDataFlow
        isOpen={isEditUserDataFlowOpen}
        onClose={() => setIsEditUserDataFlowOpen(false)}
        method={method}
      />

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isLoading={isDeletingAccount}
      />
    </div>
  );
};

export default UserDataPage;
